import configService from '@/services/configService';
import dchAPI from '@/services/dchAPI';
import config from '@/config';

const state = {
  sources: [],
  schemas: {},
};

const getters = {
  allSources: (state) => state.sources.sort((a, b) => a.name.localeCompare(b.name)),
  oneSource: (state) => (id) => {
    return state.sources.find((source) => source.id == id);
  },
  sourcesByAsset: (state) => (asset) => {
    return state.sources.filter((source) =>
      asset.sources.some((assetSource) => assetSource.handle === source.handle)
    );
  },
  enabledSources: (state) => {
    return state.sources.filter((source) => source.enabled);
  },
  sourceSchema: (state) => (handle) => state.schemas[handle],
};

// The query for the sources list. A superadmin also gets the internal sources (Ankeri's own); the API
// ignores include_internal for anyone else.
export const sourcesListQuery = (isSuperAdmin) =>
  isSuperAdmin
    ? { include_fetch_stats: true, include_internal: true }
    : { include_fetch_stats: true };

const actions = {
  async fetchSourceSchema({ state, commit }, handle) {
    if (state.schemas[handle]) {
      return state.schemas[handle];
    }
    const response = await dchAPI.get(`sources/${handle}/schema`);
    const schema = response.data.data;
    commit('setSourceSchema', { handle, schema });
    return schema;
  },
  async fetchDataFetchesBySource({ commit }, sourceHandle) {
    return new Promise((resolve, reject) => {
      dchAPI
        .get(`data/fetch?limit=10&source=${sourceHandle}`)
        .then((response) => {
          const lastFetchFailed =
            response.data.data.length > 0 && !!response.data.data[0].progress.failed;
          commit('setLastFetchFailed', { sourceHandle, lastFetchFailed });
          resolve(response);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  async fetchSourcesData({ commit, getters }, prefetchedResponse) {
    const response =
      prefetchedResponse ||
      (await dchAPI.get('sources', { params: sourcesListQuery(getters.isSuperAdmin) }));
    const responseData =
      config.IS_DEMO === 'true'
        ? addAllSources(response.data.data, getters.allConfig)
        : response.data.data.map((r) => (r = transformSourcesResponseData(r)));

    // Apply fetch stats from included data
    const fetchStatsIncluded = (response.data.included || []).find(
      (item) => item.type === 'fetch_stats'
    );
    if (fetchStatsIncluded) {
      responseData.forEach((source) => {
        const stats = fetchStatsIncluded.data[source.id];
        if (stats && stats.last_scheduled_fetch) {
          source.last_fetch_successful = !stats.last_scheduled_fetch.progress.failed;
        }
      });
    }

    commit('setSourcesData', responseData);
    return response;
  },
  async fetchSourceData({ commit, getters }, id) {
    return new Promise((resolve, reject) => {
      dchAPI
        .get(`sources/${id}`)
        .then((response) => {
          const responseData = (
            config.IS_DEMO === 'true'
              ? addAllSources([response.data.data], getters.allConfig)
              : [response.data.data]
          ).map((r) => (r = transformSourcesResponseData(r)));
          commit('updateSource', responseData.filter((r) => r.id === id)[0]);
          resolve(response);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  async fetchSourceAndRelatedData({ dispatch }, id) {
    return Promise.all([
      dispatch('fetchSourceData', id),
      dispatch('fetchAssetsBySource', id),
      dispatch('fetchOutcomesBySource', id),
      dispatch('fetchTagsBySource', id),
    ]);
  },
  async updateSource({ commit }, model) {
    const updatedSource = {
      name: model.name,
      description: model.description,
      enabled: model.enabled,
      base_url: model.base_url,
      config: model.config,
      credentials: model.credentials,
    };
    const updatedSourceFiltered = Object.keys(updatedSource)
      .filter((k) => updatedSource[k] != null)
      .reduce((a, k) => ({ ...a, [k]: updatedSource[k] }), {});
    return new Promise((resolve, reject) => {
      dchAPI
        .put(`sources/${model.id}`, updatedSourceFiltered)
        .then((response) => {
          // A 204 means nothing changed, so there is no source to commit.
          const source = response.data?.data;
          if (source) commit('mergeSource', { ...source, id: source.handle });
          resolve(response);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  async newSource({ commit }, model) {
    const newSource = {
      handle: model.id,
      name: model.name,
      description: model.description,
      config: model.config,
      credentials: model.credentials,
    };
    const newSourceFiltered = Object.keys(newSource)
      .filter((k) => newSource[k] != null)
      .reduce((a, k) => ({ ...a, [k]: newSource[k] }), {});

    return new Promise((resolve, reject) => {
      dchAPI
        .post('sources', newSourceFiltered)
        .then((response) => {
          const responseData = {
            ...response.data,
            id: response.data.handle,
            active: false,
            clicked: false,
            not_connected: false,
            central: false,
          };
          commit('newSource', responseData.data);
          resolve(response);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  async updateSourceConfig({ commit }, model) {
    const updatedSourceConfig = {
      config: model.config,
    };
    return new Promise((resolve, reject) => {
      dchAPI
        .put(`sources/${model.handle}`, updatedSourceConfig)
        .then((response) => {
          const source = response.data?.data;
          if (source) commit('mergeSource', { ...source, id: source.handle });
          resolve(response);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
};

const mutations = {
  setSourceSchema: (state, { handle, schema }) => {
    state.schemas = { ...state.schemas, [handle]: schema };
  },
  setSourcesData: (state, sources) => {
    state.sources = sources;
  },
  updateSource: (state, updatedSource) => {
    state.sources = state.sources.map((source) => {
      if (source.id === updatedSource.id) return updatedSource;
      else {
        return source;
      }
    });
  },
  // The PUT response is the stored source only, so it lacks what the list adds (fetch stats and
  // view flags). Those are kept from the entry already in the store.
  mergeSource: (state, updatedSource) => {
    state.sources = state.sources.map((source) =>
      source.id === updatedSource.id ? { ...source, ...updatedSource } : source,
    );
  },
  deleteSource: (state, id) => {
    state.sources = state.sources.filter((source) => source.id != id);
  },
  newSource: (state, newSource) => {
    state.sources = [...state.sources, newSource];
  },
  setLastFetchFailed: (state, { sourceHandle, lastFetchFailed }) => {
    const index = state.sources.findIndex((s) => s.id === sourceHandle);
    if (index > -1) {
      state.sources[index].last_fetch_successful = !lastFetchFailed;
    }
  },
};

const transformSourcesResponseData = (data) => {
  const tData = {
    ...data,
    id: data.handle,
    active: false,
    clicked: false,
    not_connected: false,
    central: false,
    is_enabled: true,
    hidden: false,
    show_only_connected_to: false,
    enabled: config.IS_DEMO === 'true' ? true : data.enabled,
  };
  return tData;
};

const addAllSources = (data, allConfig) => {
  const tData = data.map((r) => (r = transformSourcesResponseData(r)));
  const configAvailableSources = allConfig.available_sources;
  let availableSources = configService
    .availableSources()
    // A superadmin-only source has no parser, so /config doesn't list it: show it only when the
    // company has it, which the API tells only a superadmin.
    .filter((s) => !s.superadmin_only || tData.some((cs) => cs.id === s.id))
    .map(
      (s) =>
        (s = {
          ...tData.find((cs) => cs.id === s.id),
          ...s,
          active: true,
          clicked: false,
          not_connected: false,
          central: false,
          is_enabled: true,
          enabled: true,
          hidden: false,
          tags: configAvailableSources.find((cas) => cas.source_handle === s.id)?.tags ?? [],
          last_fetch_successful: true,
        })
    )
    .sort((a, b) => a.name.localeCompare(b.name));
  return availableSources;
};

export default {
  state,
  getters,
  actions,
  mutations,
};
